import React, { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface MultiSelectItem {
  id: string;
  name: string;
}

interface MultiSelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  items: MultiSelectItem[];
  placeholder?: string;
  loading?: boolean;
  className?: string;
  triggerClassName?: string;
}

export function MultiSelect({
  value = [],
  onChange,
  items = [],
  placeholder = 'Selecione...',
  loading = false,
  className,
  triggerClassName,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const removeTag = (id: string) => {
    onChange(value.filter((v) => v !== id));
  };

  const selectedItems = items.filter((item) => value.includes(item.id));
  const triggerLabel =
    selectedItems.length > 0 ? `${selectedItems.length} selecionado(s)` : placeholder;

  return (
    <div className={cn('w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={loading}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-xs shadow-input',
              triggerClassName
            )}
          >
            <span className="truncate text-left">
              {loading ? 'Carregando...' : triggerLabel}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="max-h-[300px] overflow-auto p-1">
            {items.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Nenhuma opção disponível
              </div>
            ) : (
              <ul className="space-y-0.5">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={value.includes(item.id)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-sm px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground',
                        value.includes(item.id) && 'bg-accent'
                      )}
                      onClick={() => toggle(item.id)}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          value.includes(item.id)
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-input'
                        )}
                      >
                        {value.includes(item.id) ? <Check className="h-3 w-3" /> : null}
                      </span>
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {!loading && selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 bg-black text-white px-3 py-1 rounded-full text-sm"
            >
              <span>{item.name}</span>
              <button
                type="button"
                onClick={() => removeTag(item.id)}
                className="text-gray-500 hover:text-gray-300"
                aria-label={`Remover ${item.name}`}
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
